import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useUserContext } from "context/UserContext";
import { useNavigate } from "react-router-dom";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { normalizeRoleLevel } from "utils/roleUtils";
import { deriveThirdPartyId, deriveThirdPartyName } from "layouts/agent/utils";
import taiwanCountyTopology from "assets/maps/taiwan-country.topo.json";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
);

const GET_REPORT_DASHBOARD_QUERY = `
  query GetReportDashboard($input: JSON!) {
    GetReportDashboard(input: $input)
  }
`;

const panelConfigs = {
  global: {
    key: "global",
    color: "#0A4C96",
    softColor: "#EAF3FF",
    title: "總戰情室",
    subtitle: "查看全部品牌數據",
    includePaymentFilter: true,
  },
  brand: {
    key: "brand",
    color: "#0B7A35",
    softColor: "#EAF8EF",
    title: "品牌戰情室",
    subtitle: "查看單一品牌數據",
    includePaymentFilter: false,
  },
};

const UNKNOWN_COUNTY_LABEL = "未分類";
const TAIWAN_MAP_VIEWBOX = { width: 340, height: 520 };
const TAIWAN_MAINLAND_FRAME = { x: 92, y: 18, width: 210, height: 474 };
const TAIWAN_OFFSHORE_INSETS = {
  澎湖縣: { x: 32, y: 210, width: 38, height: 54 },
  金門縣: { x: 24, y: 380, width: 50, height: 38 },
  連江縣: { x: 28, y: 114, width: 46, height: 34 },
};
const COUNTY_NAME_ALIASES = {
  臺北市: "台北市",
  臺中市: "台中市",
  臺南市: "台南市",
  臺東縣: "台東縣",
  桃園縣: "桃園市",
  桃園市: "桃園市",
};

const PAYMENT_CHART_COLORS = [
  "#2DA7F2",
  "#203249",
  "#35B45E",
  "#F2C447",
  "#CAD3DE",
  "#9C27B0",
  "#FF7A59",
  "#14B8A6",
  "#6366F1",
  "#F97316",
  "#84CC16",
  "#EC4899",
];

const READABLE_TEXT_COLOR = "#465775";
const READABLE_MUTED_TEXT_COLOR = "#6B7A93";
const DASHBOARD_TABLE_TEXT_COLOR = "#2F405A";
const DASHBOARD_BLUE = "#245EA8";
const DASHBOARD_DEEP_BLUE = "#1C4FD7";
const DASHBOARD_CARD_SX = {
  height: "100%",
  border: "1px solid #DCE5ED",
  borderRadius: "16px",
  boxShadow: "0 4px 18px rgba(33, 58, 102, 0.05)",
  backgroundColor: "#FFFFFF",
  overflow: "hidden",
};
const DASHBOARD_CARD_HEADER_SX = {
  px: { xs: 1.5, sm: 2 },
  py: 1.1,
  backgroundColor: "#F7FAFD",
  borderBottom: "1px solid #E6EDF4",
};
const DASHBOARD_CONTENT_MAX_WIDTH = 1560;
const KPI_CARD_MIN_HEIGHT = 126;
const ANALYTICS_CARD_MIN_HEIGHT = 338;
const TREND_CARD_MIN_HEIGHT = 340;
const RANKING_CARD_MIN_HEIGHT = 420;
const RESPONSIVE_CHART_HEIGHT_SX = { height: { xs: 230, sm: 260, lg: 290 } };

const dedupeOptionsByLabel = (options = []) => {
  const optionMap = new Map();

  options.forEach((option) => {
    const labelKey = String(option?.label || "")
      .trim()
      .toLowerCase();
    const valueKey = String(option?.value || "").trim();
    const key = labelKey || valueKey;
    if (!key || optionMap.has(key)) return;
    optionMap.set(key, option);
  });

  return Array.from(optionMap.values());
};

const selectSx = {
  minWidth: 110,
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#ffffff",
  },
};

const filterControlSx = {
  ...selectSx,
  minWidth: { xs: "100%", md: 180, lg: 200, xl: 220 },
  "& .MuiInputBase-input": {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
};

const autocompletePopperSx = {
  width: "max-content !important",
  minWidth: 320,
  maxWidth: "min(80vw, 560px)",
};

const autocompleteListboxSx = {
  "& .MuiAutocomplete-option": {
    whiteSpace: "normal",
    lineHeight: 1.45,
    alignItems: "flex-start",
    py: 1,
  },
};

const firstFilled = (...values) => {
  const found = values.find((value) => value !== null && value !== undefined && value !== "");
  return found === undefined ? "" : found;
};

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("zh-TW").format(Math.round(safeNumber(value)));

const formatCurrency = (value) =>
  `NT$ ${new Intl.NumberFormat("zh-TW", {
    maximumFractionDigits: 0,
  }).format(Math.round(safeNumber(value)))}`;

const buildRatioMeta = (value) => {
  const ratio = Math.round(safeNumber(value));
  if (ratio > 0) {
    return { prefix: "較前期", value: `+${ratio}% ↑`, color: "#2E9B63" };
  }
  if (ratio < 0) {
    return { prefix: "較前期", value: `${ratio}% ↓`, color: "#D63A2C" };
  }
  return { prefix: "較前期", value: "持平", color: READABLE_MUTED_TEXT_COLOR };
};

const formatDateToInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatWan = (value) => {
  const numeric = safeNumber(value) / 10000;
  return Number.isInteger(numeric) ? `${numeric}` : numeric.toFixed(1);
};

const formatDateLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(
    2,
    "0"
  )}`;
};

const formatWeekdayLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return "日一二三四五六"[date.getDay()] || "";
};

const buildRangeLabel = (start, end) => {
  if (!start || !end) return "";
  return `${formatDateLabel(start)} - ${formatDateLabel(end)}`;
};

const getPreviousPeriodRange = (startString, endString) => {
  const start = new Date(startString);
  const end = new Date(endString);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { start: "", end: "" };
  }
  const diffDays = Math.floor((end - start) / 86400000) + 1;
  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - diffDays + 1);
  return {
    start: formatDateToInputValue(prevStart),
    end: formatDateToInputValue(prevEnd),
  };
};

const normalizeDate = (value) => (value ? String(value).slice(0, 10) : "");

const getTimeSlotConfig = () => [
  { key: "overnight", label: "00:00 - 06:00", text: "凌晨・清晨", color: "#355CDE" },
  { key: "morning", label: "06:00 - 12:00", text: "上午・中午", color: "#4BAE70" },
  { key: "afternoon", label: "12:00 - 18:00", text: "下午", color: "#F08A24" },
  { key: "evening", label: "18:00 - 24:00", text: "晚上・深夜", color: "#6F4CC3" },
];

const getDefaultDateRange = () => {
  const today = new Date();
  const value = formatDateToInputValue(today);
  return { start: value, end: value };
};

const normalizePanelFilters = (filters) => {
  if (!filters.start || !filters.end) return filters;
  if (filters.start <= filters.end) return filters;
  return { ...filters, start: filters.end, end: filters.start };
};

const createGlobalFilters = () => ({
  ...getDefaultDateRange(),
  brandId: "",
  venueId: "",
  machineId: "",
  paymentMethod: "",
  salesRegion: "",
});

const createBrandFilters = (defaultBrandId = "") => ({
  ...getDefaultDateRange(),
  brandId: defaultBrandId,
  venueId: "",
  machineId: "",
  salesRegion: "",
});

const normalizeCountyName = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = raw.replace(/^臺/, "台");
  return COUNTY_NAME_ALIASES[raw] || COUNTY_NAME_ALIASES[normalized] || normalized;
};

const getGeometryBbox = (polygons) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  polygons.forEach((ring) => {
    ring.forEach(([x, y]) => {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    });
  });
  return { minX, minY, maxX, maxY };
};

const decodeTaiwanCountyTopology = (topology) => {
  const { scale, translate } = topology.transform;
  const arcCache = new Map();

  const decodeArc = (index) => {
    const cacheKey = String(index);
    if (arcCache.has(cacheKey)) return arcCache.get(cacheKey);
    const source = topology.arcs[index < 0 ? ~index : index];
    let x = 0;
    let y = 0;
    const points = source.map(([dx, dy]) => {
      x += dx;
      y += dy;
      return [translate[0] + x * scale[0], translate[1] + y * scale[1]];
    });
    const output = index < 0 ? [...points].reverse() : points;
    arcCache.set(cacheKey, output);
    return output;
  };

  const decodeRing = (arcIndices) =>
    arcIndices.reduce((points, arcIndex, arcOffset) => {
      const nextPoints = decodeArc(arcIndex);
      return points.concat(arcOffset > 0 ? nextPoints.slice(1) : nextPoints);
    }, []);

  return topology.objects.map.geometries.map((geometry) => {
    const polygons =
      geometry.type === "Polygon"
        ? geometry.arcs.map((ring) => decodeRing(ring))
        : geometry.arcs.flatMap((polygon) => polygon.map((ring) => decodeRing(ring)));
    return {
      name: normalizeCountyName(geometry.properties?.name),
      polygons,
    };
  });
};

const transformPolygons = (polygons, transformPoint) =>
  polygons.map((ring) => ring.map(([x, y]) => transformPoint(x, y)));

const polygonsToSvgPath = (polygons) =>
  polygons
    .map((ring) =>
      ring.length
        ? `M ${ring.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join(" L ")} Z`
        : ""
    )
    .join(" ");

const buildTaiwanCountyMapShapes = () => {
  const decoded = decodeTaiwanCountyTopology(taiwanCountyTopology).map((county) => ({
    ...county,
    bbox: getGeometryBbox(county.polygons),
  }));
  const mainlandCounties = decoded.filter((county) => !TAIWAN_OFFSHORE_INSETS[county.name]);
  const mainlandBounds = mainlandCounties.reduce(
    (bounds, county) => ({
      minX: Math.min(bounds.minX, county.bbox.minX),
      minY: Math.min(bounds.minY, county.bbox.minY),
      maxX: Math.max(bounds.maxX, county.bbox.maxX),
      maxY: Math.max(bounds.maxY, county.bbox.maxY),
    }),
    { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
  );
  const scaleX = TAIWAN_MAINLAND_FRAME.width / (mainlandBounds.maxX - mainlandBounds.minX);
  const scaleY = TAIWAN_MAINLAND_FRAME.height / (mainlandBounds.maxY - mainlandBounds.minY);
  const mainlandScale = Math.min(scaleX, scaleY);
  const mainlandOffsetX =
    TAIWAN_MAINLAND_FRAME.x +
    (TAIWAN_MAINLAND_FRAME.width - (mainlandBounds.maxX - mainlandBounds.minX) * mainlandScale) / 2;
  const mainlandOffsetY =
    TAIWAN_MAINLAND_FRAME.y +
    (TAIWAN_MAINLAND_FRAME.height - (mainlandBounds.maxY - mainlandBounds.minY) * mainlandScale) /
      2;

  return decoded.map((county) => {
    const inset = TAIWAN_OFFSHORE_INSETS[county.name];
    const transformedPolygons = inset
      ? transformPolygons(county.polygons, (x, y) => {
          const bbox = county.bbox;
          const countyScale = Math.min(
            inset.width / Math.max(0.0001, bbox.maxX - bbox.minX),
            inset.height / Math.max(0.0001, bbox.maxY - bbox.minY)
          );
          const countyOffsetX = inset.x + (inset.width - (bbox.maxX - bbox.minX) * countyScale) / 2;
          const countyOffsetY =
            inset.y + (inset.height - (bbox.maxY - bbox.minY) * countyScale) / 2;
          return [
            countyOffsetX + (x - bbox.minX) * countyScale,
            countyOffsetY + (bbox.maxY - y) * countyScale,
          ];
        })
      : transformPolygons(county.polygons, (x, y) => [
          mainlandOffsetX + (x - mainlandBounds.minX) * mainlandScale,
          mainlandOffsetY + (mainlandBounds.maxY - y) * mainlandScale,
        ]);
    return {
      name: county.name,
      path: polygonsToSvgPath(transformedPolygons),
    };
  });
};

const createEmptyDashboardData = () => ({
  summary: {},
  top_venues: [],
  top_machines: [],
  top_products: [],
  payment: { total_count: 0, breakdown: [] },
  inventory: {},
  filters: {},
  sales_by_weekday: [],
  sales_by_time_slot: [],
  daily_sales_trend: [],
  low_stock_machine_list: [],
  options: {},
});

const createEmptyViewData = () => ({
  periodKpis: [
    { label: "訂單數（區間）", value: "0", icon: "receipt_long", note: "較前期 持平" },
    { label: "銷售額（區間）", value: formatCurrency(0), icon: "payments", note: "較前期 持平" },
    {
      label: "平均單價（區間）",
      value: formatCurrency(0),
      icon: "shopping_cart",
      note: "較前期 持平",
    },
    {
      label: "平均每台日均（區間）",
      value: formatCurrency(0),
      icon: "trending_up",
      note: "較前期 持平",
    },
  ],
  realtimeKpis: [
    { label: "品牌數", value: "0", icon: "storefront" },
    { label: "場域數", value: "0", icon: "place" },
    { label: "線上機台數（即時）", value: "0", icon: "dns", note: "離線機台數 0 台" },
    {
      label: "低庫存及缺貨機台倉（即時）",
      value: "0 / 0",
      icon: "inventory_2",
      note: "低庫存 / 缺貨",
    },
  ],
  timeSlotRows: [],
  dailyTrendRows: [],
  weekdayTrend: { current: [], previous: [], labels: [] },
  inventoryRows: [],
  topVenues: [],
  topMachines: [],
  topProducts: [],
  rangeLabel: "",
  previousRangeLabel: "",
  monthLabel: "",
});

const fetchGraphQL = async (backendUrl, query, variables = {}) => {
  const response = await fetch(backendUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(json?.message || `Request failed (${response.status})`);
  }
  if (Array.isArray(json?.errors) && json.errors.length) {
    throw new Error(json.errors[0]?.message || "GraphQL request failed");
  }
  return json?.data || {};
};

const normalizeOptionText = (value) => String(value || "").trim();

const buildFilterOptions = (options, allLabel) => [
  { value: "", label: allLabel },
  ...options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option
  ),
];

const normalizePaymentMethodOption = (option) => {
  if (typeof option === "string") {
    const value = option.trim();
    return value ? { value, label: value } : null;
  }

  const value = String(option?.value || option?.payment_method || "").trim();
  if (!value) return null;
  const label = String(option?.label || value).trim();
  return { value, label: label || value };
};

const normalizeDashboardOptions = (options = {}) => ({
  brands: dedupeOptionsByLabel(
    (Array.isArray(options?.brands) ? options.brands : [])
      .map((item) => {
        const value = normalizeOptionText(item?.value);
        const label = normalizeOptionText(item?.label || item?.value);
        if (!value || !label) return null;
        return { value, label };
      })
      .filter(Boolean)
  ).sort((a, b) => a.label.localeCompare(b.label)),
  venues: dedupeOptionsByLabel(
    (Array.isArray(options?.venues) ? options.venues : [])
      .map((item) => {
        const value = normalizeOptionText(item?.value);
        if (!value) return null;
        return {
          value,
          label: normalizeOptionText(item?.label || item?.value),
          brandId: normalizeOptionText(item?.brand_id),
          brandLabel: normalizeOptionText(item?.brand_name),
        };
      })
      .filter(Boolean)
  ).sort((a, b) => a.label.localeCompare(b.label)),
  machines: dedupeOptionsByLabel(
    (Array.isArray(options?.machines) ? options.machines : [])
      .map((item) => {
        const value = normalizeOptionText(item?.value);
        if (!value) return null;
        return {
          value,
          label: normalizeOptionText(item?.label || item?.machine_name || item?.value),
          brandId: normalizeOptionText(item?.brand_id),
          venueId: normalizeOptionText(item?.venue_id),
          venueLabel: normalizeOptionText(item?.venue_name),
          brandLabel: normalizeOptionText(item?.brand_name),
          ref: normalizeOptionText(item?.device_id || item?.machine_name),
        };
      })
      .filter(Boolean)
  ).sort((a, b) => a.label.localeCompare(b.label)),
  paymentMethods: dedupeOptionsByLabel(
    (Array.isArray(options?.payment_methods) ? options.payment_methods : [])
      .map(normalizePaymentMethodOption)
      .filter(Boolean)
  ).sort((a, b) => a.label.localeCompare(b.label)),
  salesRegions: dedupeOptionsByLabel(
    (Array.isArray(options?.sales_by_region) ? options.sales_by_region : [])
      .map((item) => {
        if (typeof item === "string") {
          const value = normalizeOptionText(item);
          return value ? { value, label: value } : null;
        }

        const value = normalizeOptionText(item?.value || item?.label);
        const label = normalizeOptionText(item?.label || item?.value);
        return value ? { value, label: label || value } : null;
      })
      .filter(Boolean)
  ).sort((a, b) => a.label.localeCompare(b.label)),
});

const buildOptionLabelMap = (options = []) =>
  (Array.isArray(options) ? options : []).reduce((acc, option) => {
    const normalized = normalizePaymentMethodOption(option);
    if (normalized?.value) {
      acc[normalized.value] = normalized.label;
    }
    return acc;
  }, {});

const buildDashboardRequestBody = (email, filters, includePaymentFilter) => {
  const body = {
    email,
    start: filters.start,
    end: filters.end,
  };
  if (filters.brandId) body.brand_ids = [filters.brandId];
  if (filters.venueId) body.venue_ids = [filters.venueId];
  if (filters.machineId) body.machine_ids = [filters.machineId];
  if (includePaymentFilter && filters.paymentMethod) {
    body.payment_methods = [filters.paymentMethod];
  }
  if (filters.salesRegion) {
    body.sales_by_region = [filters.salesRegion];
  }
  return body;
};

const buildViewData = (dashboardData, filters, navigate) => {
  const summary = dashboardData?.summary || {};
  const inventory = dashboardData?.inventory || {};
  const paymentLabelMap = buildOptionLabelMap(dashboardData?.options?.payment_methods);
  const timeSlotConfig = getTimeSlotConfig();
  const totalSalesAmount = safeNumber(summary.sales_amount);
  const timeSlotRows = timeSlotConfig.map((slot, index) => {
    const apiRows = Array.isArray(dashboardData?.sales_by_time_slot)
      ? dashboardData.sales_by_time_slot
      : [];
    const match =
      apiRows[index] ||
      apiRows.find(
        (item) =>
          String(item?.label || "").includes(slot.label.split(" - ")[0]) ||
          String(item?.start_time || "") ===
            (slot.key === "overnight"
              ? "00:00:00"
              : slot.key === "morning"
              ? "06:00:00"
              : slot.key === "afternoon"
              ? "12:00:00"
              : "18:00:00")
      ) ||
      {};
    const amount = safeNumber(match?.total_amount);
    return {
      ...slot,
      amount,
      ratio: totalSalesAmount ? Math.round((amount / totalSalesAmount) * 100) : 0,
    };
  });

  const inventorySortWeight = { low_stock: 0, empty: 1 };
  const realtimeInventoryRows = (
    Array.isArray(dashboardData?.low_stock_machine_list) ? dashboardData.low_stock_machine_list : []
  )
    .sort((a, b) => {
      const statusDiff =
        (inventorySortWeight[a.status] ?? Number.MAX_SAFE_INTEGER) -
        (inventorySortWeight[b.status] ?? Number.MAX_SAFE_INTEGER);
      if (statusDiff !== 0) return statusDiff;
      return safeNumber(a.current_qty) - safeNumber(b.current_qty);
    })
    .map((row) => ({
      machine: [firstFilled(row?.venue_name), firstFilled(row?.machine_name, row?.device_id, "-")]
        .filter(Boolean)
        .join(" / "),
      status: firstFilled(row?.status_label, row?.status === "empty" ? "缺貨" : "低庫存"),
      qty: formatNumber(row?.current_qty),
    }));

  const dailyTrendRows = (
    Array.isArray(dashboardData?.daily_sales_trend) ? dashboardData.daily_sales_trend : []
  ).map((row) => ({
    date: String(row?.date || ""),
    label: formatDateLabel(row?.date),
    weekday: String(row?.weekday || ""),
    amount: safeNumber(row?.total_amount),
    orders: safeNumber(row?.order_count),
  }));

  const previousRange = getPreviousPeriodRange(filters?.start, filters?.end);
  const weekdayRows = Array.isArray(dashboardData?.sales_by_weekday)
    ? dashboardData.sales_by_weekday
    : [];
  const paymentBreakdown = Array.isArray(dashboardData?.payment?.breakdown)
    ? dashboardData.payment.breakdown
    : [];
  const topVenues = (Array.isArray(dashboardData?.top_venues) ? dashboardData.top_venues : []).map(
    (item) => ({
      label: firstFilled(item?.name, item?.id, "未命名場域"),
      amount: formatCurrency(item?.total_amount),
      orders: formatNumber(item?.order_count),
    })
  );

  const topMachines = (
    Array.isArray(dashboardData?.top_machines) ? dashboardData.top_machines : []
  ).map((item) => ({
    label: firstFilled(item?.machine_name, item?.name, item?.device_id, "未命名機台"),
    amount: formatCurrency(item?.total_amount),
    orders: formatNumber(item?.order_count),
  }));

  const topProducts = (
    Array.isArray(dashboardData?.top_products) ? dashboardData.top_products : []
  ).map((item) => {
    const rawName = String(firstFilled(item?.name, item?.sku, "未命名商品")).trim();
    const [namePart = "", pricePart = ""] = rawName.split(" / $");
    const fallbackName = String(firstFilled(item?.sku, item?.name, "未命名商品")).trim();
    const resolvedName = namePart.trim() || fallbackName;
    const resolvedPrice = pricePart.trim()
      ? `$${pricePart.trim()}`
      : safeNumber(item?.price) > 0
      ? `$${safeNumber(item?.price)}`
      : "-";

    return {
      label: resolvedName,
      price: resolvedPrice,
      quantity: formatNumber(item?.quantity),
      amount: formatCurrency(item?.total_amount),
    };
  });

  return {
    periodKpis: [
      {
        label: "訂單數（區間）",
        value: formatNumber(summary.order_count),
        icon: "receipt_long",
        noteMeta: buildRatioMeta(summary.order_count_change_ratio),
      },
      {
        label: "銷售額（區間）",
        value: formatCurrency(summary.sales_amount),
        icon: "payments",
        noteMeta: buildRatioMeta(summary.sales_amount_change_ratio),
      },
      {
        label: "平均單價（區間）",
        value: formatCurrency(summary.average_order_amount),
        icon: "shopping_cart",
        noteMeta: buildRatioMeta(summary.average_order_amount_change_ratio),
      },
      {
        label: "平均每台日均（區間）",
        value: formatCurrency(summary.average_machine_day_sales),
        icon: "trending_up",
        noteMeta: buildRatioMeta(summary.average_machine_day_sales_change_ratio),
      },
    ],
    realtimeKpis: [
      {
        label: "品牌數（即時）",
        value: formatNumber(summary.brand_count),
        icon: "storefront",
      },
      {
        label: "場域數（即時）",
        value: formatNumber(summary.venue_count),
        icon: "place",
      },
      {
        label: "線上機台數（即時）",
        value: String(summary.online_machine_count_label || summary.online_machine_count || "0"),
        icon: "dns",
        noteMeta: {
          prefix: "離線機台數",
          value: String(
            summary.offline_machine_count_label || summary.offline_machine_count || "0"
          ),
          color: READABLE_MUTED_TEXT_COLOR,
        },
      },
      {
        label: "低庫存及缺貨機台倉（即時）",
        value: formatNumber(inventory.low_stock_and_empty_count || 0),
        icon: "inventory_2",
        noteMeta: {
          prefix: "低庫存 / 缺貨",
          value: `${formatNumber(
            inventory.low_stock_warehouse_count ?? inventory.low_stock
          )} / ${formatNumber(inventory.empty_warehouse_count ?? inventory.empty)}`,
          color: READABLE_MUTED_TEXT_COLOR,
        },
        linkNote: "查看機台列表 >",
        onLinkClick: navigate ? () => navigate("/warehouses-realtime") : undefined,
      },
    ],
    timeSlotRows,
    dailyTrendRows,
    weekdayTrend: {
      labels: weekdayRows.map((item) => String(item?.weekday || "")),
      current: weekdayRows.map((item) => safeNumber(item?.current_total_amount_ten_thousands)),
      previous: weekdayRows.map((item) => safeNumber(item?.previous_total_amount_ten_thousands)),
    },
    payments: paymentBreakdown.map((item) => [
      paymentLabelMap[item?.payment_method] || item?.payment_method || "其他",
      safeNumber(item?.amount_ratio || item?.ratio),
    ]),
    paymentDetails: paymentBreakdown.map((item) => ({
      label: paymentLabelMap[item?.payment_method] || item?.payment_method || "其他",
      amount: formatCurrency(item?.total_amount),
      ratio: `${Math.round(safeNumber(item?.amount_ratio || item?.ratio))}%`,
    })),
    paymentRangeLabel: buildRangeLabel(filters?.start, filters?.end),
    paymentTotalAmount: formatCurrency(dashboardData?.payment?.total_amount),
    inventoryRows: realtimeInventoryRows,
    topVenues,
    topMachines,
    topProducts,
    rangeLabel: buildRangeLabel(filters?.start, filters?.end),
    previousRangeLabel: buildRangeLabel(previousRange.start, previousRange.end),
    monthLabel: filters?.end?.slice?.(0, 7) || "",
  };
};

function KPIItem({ item, color }) {
  return (
    <MDBox
      sx={{
        position: "relative",
        p: { xs: 1.5, sm: 2 },
        borderRight: { sm: "1px solid #E5EDF5" },
        borderBottom: { xs: "1px solid #E5EDF5", lg: "none" },
        minHeight: KPI_CARD_MIN_HEIGHT,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <MDBox
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          backgroundColor: `${color}14`,
          mb: 1,
        }}
      >
        <Icon fontSize="small">{item.icon}</Icon>
      </MDBox>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        sx={{ color: READABLE_TEXT_COLOR, fontSize: "0.82rem", letterSpacing: "0.01em" }}
      >
        {item.label}
      </MDTypography>
      <MDTypography
        variant="h5"
        fontWeight="bold"
        sx={{
          color,
          mt: 0.9,
          fontSize: {
            xs: item.value.length > 12 ? "0.98rem" : "1.42rem",
            sm: item.value.length > 12 ? "1.05rem" : "1.7rem",
          },
          lineHeight: 1.1,
          overflowWrap: "anywhere",
        }}
      >
        {item.value}
      </MDTypography>
      {item.noteMeta ? (
        <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: "auto", pt: 1 }}>
          <MDTypography variant="caption" sx={{ color: READABLE_MUTED_TEXT_COLOR }}>
            {item.noteMeta.prefix}
          </MDTypography>
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{ color: item.noteMeta.color || READABLE_MUTED_TEXT_COLOR }}
          >
            {item.noteMeta.value}
          </MDTypography>
        </MDBox>
      ) : null}
      {item.linkNote ? (
        <MDTypography
          variant="caption"
          fontWeight="bold"
          onClick={item.onLinkClick}
          sx={{
            color: DASHBOARD_BLUE,
            display: "block",
            mt: "auto",
            pt: 1,
            cursor: item.onLinkClick ? "pointer" : "default",
          }}
        >
          {item.linkNote}
        </MDTypography>
      ) : null}
      {item.note ? (
        <MDTypography
          variant="caption"
          sx={{ color: READABLE_MUTED_TEXT_COLOR, display: "block", mt: 0.5 }}
        >
          {item.note}
        </MDTypography>
      ) : null}
    </MDBox>
  );
}

function SectionHeader({ title }) {
  return (
    <MDBox
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 1,
      }}
    >
      <MDTypography
        variant="button"
        fontWeight="bold"
        sx={{
          color: DASHBOARD_BLUE,
          fontSize: "0.98rem",
          lineHeight: 1.2,
        }}
      >
        {title}
      </MDTypography>
    </MDBox>
  );
}

function KPISection({ title, items, color }) {
  return (
    <MDBox>
      <SectionHeader title={title} />
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: `repeat(${items.length}, minmax(0, 1fr))`,
          },
          border: "1px solid #DCE5ED",
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        {items.map((item) => (
          <MDBox key={item.label}>
            <KPIItem item={item} color={color} />
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  );
}

function TimeSlotAnalysisCard({ title, rangeLabel, rows }) {
  const chartData = {
    labels: rows.map((row) => row.label),
    datasets: [
      {
        data: rows.map((row) => row.amount),
        backgroundColor: rows.map((row) => row.color),
        borderWidth: 0,
      },
    ],
  };

  return (
    <Card sx={{ ...DASHBOARD_CARD_SX, minHeight: ANALYTICS_CARD_MIN_HEIGHT }}>
      <MDBox sx={DASHBOARD_CARD_HEADER_SX}>
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: READABLE_TEXT_COLOR, fontSize: "1rem" }}
        >
          {title}
        </MDTypography>
        <MDTypography
          variant="caption"
          sx={{
            color: READABLE_MUTED_TEXT_COLOR,
            display: "block",
            mt: 0.35,
            fontWeight: 700,
          }}
        >
          區間：{rangeLabel || "-"}
        </MDTypography>
      </MDBox>
      <MDBox
        p={2.25}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1.5, sm: 2 },
          minHeight: { xs: 250, sm: 280 },
        }}
      >
        <MDBox
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <MDBox sx={{ width: { xs: 188, sm: 220 }, height: { xs: 188, sm: 220 } }}>
            <Doughnut
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                cutout: "62%",
              }}
            />
          </MDBox>
        </MDBox>
        <Stack spacing={1.1}>
          {rows.map((row) => (
            <MDBox
              key={row.key}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "12px minmax(0, 1fr)", sm: "12px minmax(0, 1fr) auto" },
                gap: 1,
                alignItems: "start",
                borderTop: "1px solid #EEF3F7",
                pt: 1,
              }}
            >
              <MDBox
                sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: row.color }}
              />
              <MDBox>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  sx={{ color: DASHBOARD_TABLE_TEXT_COLOR, fontSize: "0.92rem", lineHeight: 1.4 }}
                >
                  {row.label}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  sx={{
                    color: DASHBOARD_TABLE_TEXT_COLOR,
                    display: "block",
                    fontSize: "0.82rem",
                  }}
                >
                  {row.text}
                </MDTypography>
              </MDBox>
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{
                  color: DASHBOARD_TABLE_TEXT_COLOR,
                  gridColumn: { xs: "2 / -1", sm: "auto" },
                  textAlign: { xs: "left", sm: "right" },
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  fontSize: "0.92rem",
                  lineHeight: 1.35,
                }}
              >
                {formatCurrency(row.amount)} ({row.ratio}%)
              </MDTypography>
            </MDBox>
          ))}
        </Stack>
      </MDBox>
    </Card>
  );
}

function InventoryListCard({ rows }) {
  const gridTemplateColumns = {
    xs: "minmax(0, 1fr) 58px 54px",
    sm: "minmax(0, 1fr) 88px 72px",
  };

  return (
    <Card sx={{ ...DASHBOARD_CARD_SX, minHeight: ANALYTICS_CARD_MIN_HEIGHT }}>
      <MDBox sx={DASHBOARD_CARD_HEADER_SX}>
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: READABLE_TEXT_COLOR, fontSize: "1rem" }}
        >
          低庫存及/缺貨機台列表（即時）
        </MDTypography>
      </MDBox>
      {rows.length ? (
        <MDBox sx={{ width: "100%", overflow: "hidden" }}>
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns,
              alignItems: "start",
              px: 1.5,
              py: 1.1,
              borderBottom: "1px solid #E7EEF5",
            }}
          >
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{
                color: DASHBOARD_TABLE_TEXT_COLOR,
                fontSize: "0.78rem",
                lineHeight: 1.2,
                pr: 1,
              }}
            >
              場域 / 機台
            </MDTypography>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{
                color: DASHBOARD_TABLE_TEXT_COLOR,
                fontSize: "0.78rem",
                lineHeight: 1.2,
                pr: 1,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              狀態
            </MDTypography>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{
                color: DASHBOARD_TABLE_TEXT_COLOR,
                fontSize: "0.78rem",
                lineHeight: 1.2,
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              剩餘庫存
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              maxHeight: { xs: 360, md: 420, xl: 520 },
              overflowY: "auto",
              overscrollBehavior: "contain",
            }}
          >
            {rows.map((row) => (
              <MDBox
                key={`${row.machine}-${row.status}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns,
                  alignItems: "start",
                  px: 1.5,
                  py: 1.1,
                  borderBottom: "1px solid #E7EEF5",
                }}
              >
                <MDTypography
                  variant="button"
                  sx={{
                    color: DASHBOARD_TABLE_TEXT_COLOR,
                    fontSize: { xs: "0.76rem", sm: "0.82rem" },
                    fontWeight: 600,
                    lineHeight: 1.38,
                    pr: 1,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {row.machine}
                </MDTypography>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  sx={{
                    color: row.status === "缺貨" ? "#D63A2C" : "#F08A24",
                    fontSize: { xs: "0.76rem", sm: "0.82rem" },
                    lineHeight: 1.3,
                    pr: 1,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.status}
                </MDTypography>
                <MDTypography
                  variant="button"
                  sx={{
                    color: DASHBOARD_TABLE_TEXT_COLOR,
                    fontSize: { xs: "0.76rem", sm: "0.82rem" },
                    fontWeight: 600,
                    lineHeight: 1.3,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {row.qty}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      ) : (
        <MDBox p={2.5}>
          <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
            目前沒有低庫存或缺貨機台。
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

function DailyRevenueTrendCard({ rows }) {
  const chartData = {
    labels: rows.map((row) => row.label),
    datasets: [
      {
        label: "營收（萬元）",
        data: rows.map((row) => Number(formatWan(row.amount))),
        backgroundColor: rows.map((row) =>
          ["六", "日"].includes(row.weekday) ? "#59A869" : "#355CDE"
        ),
        borderRadius: 8,
        maxBarThickness: 36,
      },
    ],
  };

  return (
    <Card sx={{ ...DASHBOARD_CARD_SX, minHeight: TREND_CARD_MIN_HEIGHT }}>
      <MDBox sx={DASHBOARD_CARD_HEADER_SX}>
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: READABLE_TEXT_COLOR, fontSize: "1rem" }}
        >
          當月日營收趨勢
        </MDTypography>
      </MDBox>
      <MDBox p={2.25}>
        <MDTypography
          variant="caption"
          sx={{ color: READABLE_MUTED_TEXT_COLOR, display: "block", mb: 1.5 }}
        >
          單位：萬元
        </MDTypography>
        <MDBox sx={RESPONSIVE_CHART_HEIGHT_SX}>
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    title: (items) => {
                      const row = rows[items[0]?.dataIndex || 0];
                      return `${row?.label || ""}（${row?.weekday || ""}）`;
                    },
                    label: (context) => {
                      const row = rows[context.dataIndex];
                      return [
                        `銷售額：${formatCurrency(row?.amount)}`,
                        `訂單數：${formatNumber(row?.orders)}`,
                      ];
                    },
                  },
                },
              },
              scales: {
                y: { ticks: { callback: (value) => `${value}` }, grid: { color: "#EDF2F7" } },
                x: { grid: { display: false } },
              },
            }}
          />
        </MDBox>
        <MDBox
          sx={{
            mt: 1.5,
            display: "flex",
            justifyContent: "center",
            gap: 2.5,
            flexWrap: "wrap",
          }}
        >
          <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <MDBox
              sx={{
                width: 12,
                height: 12,
                borderRadius: "3px",
                backgroundColor: "#355CDE",
              }}
            />
            <MDTypography variant="caption" sx={{ color: READABLE_TEXT_COLOR }}>
              平日
            </MDTypography>
          </MDBox>
          <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <MDBox
              sx={{
                width: 12,
                height: 12,
                borderRadius: "3px",
                backgroundColor: "#59A869",
              }}
            />
            <MDTypography variant="caption" sx={{ color: READABLE_TEXT_COLOR }}>
              週六、週日
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

function WeekdayTrendCard({ rangeLabel, previousRangeLabel, trend }) {
  return (
    <Card sx={{ ...DASHBOARD_CARD_SX, minHeight: TREND_CARD_MIN_HEIGHT }}>
      <MDBox sx={DASHBOARD_CARD_HEADER_SX}>
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: READABLE_TEXT_COLOR, fontSize: "1rem" }}
        >
          星期營收分析
        </MDTypography>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          sx={{ color: READABLE_MUTED_TEXT_COLOR, display: "block", mt: 0.35 }}
        >
          區間：{rangeLabel || "-"}
        </MDTypography>
      </MDBox>
      <MDBox p={2.25}>
        <MDTypography
          variant="caption"
          sx={{ color: READABLE_MUTED_TEXT_COLOR, display: "block", mb: 1.5 }}
        >
          單位：萬元
        </MDTypography>
        <MDBox sx={RESPONSIVE_CHART_HEIGHT_SX}>
          <Line
            data={{
              labels: trend.labels,
              datasets: [
                {
                  label: `本期（${rangeLabel || "-"}）`,
                  data: trend.current,
                  borderColor: "#355CDE",
                  backgroundColor: "#355CDE",
                  tension: 0.35,
                },
                {
                  label: `前期（${previousRangeLabel || "-"}）`,
                  data: trend.previous,
                  borderColor: "#9AA8B8",
                  backgroundColor: "#9AA8B8",
                  borderDash: [6, 6],
                  tension: 0.35,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { position: "bottom" } },
              scales: {
                y: { grid: { color: "#EDF2F7" } },
                x: { grid: { display: false } },
              },
            }}
          />
        </MDBox>
      </MDBox>
    </Card>
  );
}

function RankingTableCard({ title, columns, rows, emptyText }) {
  const resolvedColumns = columns.map((column, index) => {
    if (column.width) {
      return {
        ...column,
        width: typeof column.width === "number" ? `${column.width}px` : column.width,
      };
    }

    if (index === 0) return { ...column, width: "10%" };
    if (column.key === "label") return { ...column, width: "1fr" };
    return { ...column, width: "20%" };
  });

  const gridTemplateColumns = resolvedColumns
    .map((column) => (column.width === "1fr" ? "minmax(0, 1fr)" : column.width))
    .join(" ");
  const isProductTable = columns.length >= 5;
  const headerFontSize = isProductTable ? "0.74rem" : "0.78rem";
  const bodyFontSize = isProductTable ? "0.82rem" : "0.86rem";
  const labelBodyFontSize = isProductTable ? "0.8rem" : "0.84rem";
  const mobileMetricColumns = resolvedColumns.filter(
    (column) => !["rank", "label"].includes(column.key)
  );

  return (
    <Card sx={{ ...DASHBOARD_CARD_SX, minHeight: RANKING_CARD_MIN_HEIGHT }}>
      <MDBox sx={DASHBOARD_CARD_HEADER_SX}>
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: READABLE_TEXT_COLOR, fontSize: "1rem" }}
        >
          {title}
        </MDTypography>
      </MDBox>
      {rows.length ? (
        <>
          <MDBox sx={{ display: { xs: "block", lg: "none" } }}>
            {rows.map((row, index) => (
              <MDBox
                key={`${title}-${row.label}-${index}-mobile`}
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderBottom: "1px solid #E7EEF5",
                }}
              >
                <MDBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "34px minmax(0, 1fr)",
                    gap: 1,
                    alignItems: "start",
                  }}
                >
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{
                      color: DASHBOARD_TABLE_TEXT_COLOR,
                      fontSize: "0.94rem",
                      lineHeight: 1.4,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {index + 1}
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{
                      color: DASHBOARD_TABLE_TEXT_COLOR,
                      fontSize: "0.92rem",
                      lineHeight: 1.45,
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {row.label}
                  </MDTypography>
                </MDBox>
                <MDBox
                  sx={{
                    pl: "42px",
                    mt: 0.75,
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 0.75,
                  }}
                >
                  {mobileMetricColumns.map((column) => (
                    <MDBox key={`${title}-${index}-${column.key}-mobile`}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{
                          color: READABLE_MUTED_TEXT_COLOR,
                          display: "block",
                          fontSize: "0.72rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {column.label}
                      </MDTypography>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{
                          color: DASHBOARD_TABLE_TEXT_COLOR,
                          display: "block",
                          fontSize: "0.84rem",
                          lineHeight: 1.35,
                          fontVariantNumeric: "tabular-nums",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {row[column.key]}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
            ))}
          </MDBox>

          <MDBox
            sx={{
              display: { xs: "none", lg: "block" },
              width: "100%",
              overflow: "hidden",
            }}
          >
            <MDBox
              sx={{
                display: "grid",
                gridTemplateColumns,
                columnGap: 0,
                alignItems: "start",
                px: 1.5,
                py: 1.1,
                borderBottom: "1px solid #E7EEF5",
              }}
            >
              {resolvedColumns.map((column) => (
                <MDTypography
                  key={column.key}
                  variant="caption"
                  fontWeight="bold"
                  sx={{
                    color: DASHBOARD_TABLE_TEXT_COLOR,
                    fontSize: headerFontSize,
                    lineHeight: 1.25,
                    pr: 1,
                    textAlign: column.align || "left",
                    whiteSpace: "nowrap",
                  }}
                >
                  {column.label}
                </MDTypography>
              ))}
            </MDBox>
            <MDBox>
              {rows.map((row, index) => (
                <MDBox
                  key={`${title}-${row.label}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns,
                    columnGap: 0,
                    alignItems: "start",
                    px: 1.5,
                    py: 1.1,
                    borderBottom: "1px solid #E7EEF5",
                  }}
                >
                  {resolvedColumns.map((column, columnIndex) => {
                    const value = columnIndex === 0 ? index + 1 : row[column.key];
                    const isLabelColumn = column.key === "label";
                    const isNumericColumn =
                      column.align === "right" ||
                      ["amount", "orders", "price", "quantity", "rank"].includes(column.key);

                    return (
                      <MDTypography
                        key={`${title}-${index}-${column.key}`}
                        variant="button"
                        fontWeight="medium"
                        sx={{
                          color: DASHBOARD_TABLE_TEXT_COLOR,
                          fontSize: isLabelColumn ? labelBodyFontSize : bodyFontSize,
                          lineHeight: isLabelColumn ? 1.38 : 1.3,
                          pr: 1,
                          textAlign: column.align || "left",
                          whiteSpace: isLabelColumn ? "normal" : "nowrap",
                          wordBreak: isLabelColumn ? "break-word" : "keep-all",
                          overflowWrap: isLabelColumn ? "anywhere" : "normal",
                          fontVariantNumeric: isNumericColumn ? "tabular-nums" : "normal",
                        }}
                      >
                        {value}
                      </MDTypography>
                    );
                  })}
                </MDBox>
              ))}
            </MDBox>
          </MDBox>
        </>
      ) : (
        <MDBox p={2.5}>
          <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
            {emptyText}
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

function RankCard({ title, subtitle, rows, accent, emptyText }) {
  return (
    <Card sx={{ height: "100%", border: "1px solid #DCE5ED", boxShadow: "none" }}>
      <MDBox
        px={2}
        py={1.25}
        sx={{ backgroundColor: "#F7FAFD", borderBottom: "1px solid #E6EDF4" }}
      >
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: accent, fontSize: "1rem", lineHeight: 1.4 }}
        >
          {title}
        </MDTypography>
        {subtitle ? (
          <MDTypography
            variant="caption"
            sx={{
              color: READABLE_MUTED_TEXT_COLOR,
              display: "block",
              mt: 0.35,
              fontSize: "0.92rem",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </MDTypography>
        ) : null}
      </MDBox>
      {rows.length ? (
        <MDBox component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {rows.map(([label, value], index) => (
            <MDBox
              component="li"
              key={`${title}-${label}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "28px 1fr auto",
                gap: 1,
                px: 2,
                py: 1.35,
                alignItems: "center",
                borderBottom: index === rows.length - 1 ? "none" : "1px solid #EEF3F7",
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{
                  color: index < 3 ? "#A05C00" : READABLE_MUTED_TEXT_COLOR,
                  fontSize: "0.96rem",
                }}
              >
                {index + 1}
              </MDTypography>
              <MDTypography
                variant="button"
                sx={{ color: READABLE_TEXT_COLOR, fontSize: "0.96rem", lineHeight: 1.55 }}
              >
                {label}
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{ color: READABLE_TEXT_COLOR, fontSize: "0.96rem", whiteSpace: "nowrap" }}
              >
                {value}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      ) : (
        <MDBox p={2.5}>
          <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
            {emptyText}
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

function PaymentChart({ title, rangeLabel, totalAmount, rows, details }) {
  if (!rows.length) {
    return (
      <Card sx={{ ...DASHBOARD_CARD_SX, minHeight: ANALYTICS_CARD_MIN_HEIGHT }}>
        <MDBox sx={DASHBOARD_CARD_HEADER_SX}>
          <MDTypography variant="button" fontWeight="bold">
            {title}
          </MDTypography>
          <MDTypography
            variant="caption"
            sx={{ color: READABLE_MUTED_TEXT_COLOR, display: "block", mt: 0.35 }}
          >
            區間：{rangeLabel || "-"}
          </MDTypography>
        </MDBox>
        <MDBox p={2.5}>
          <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
            目前沒有可用的支付資料。
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  const chartData = {
    labels: rows.map(([label]) => label),
    datasets: [
      {
        data: rows.map(([, value]) => value),
        backgroundColor: rows.map(
          (_, index) => PAYMENT_CHART_COLORS[index % PAYMENT_CHART_COLORS.length]
        ),
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: "circle" },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${Math.round(safeNumber(context.raw))}%`,
        },
      },
    },
    cutout: "62%",
  };

  return (
    <Card sx={{ ...DASHBOARD_CARD_SX, minHeight: ANALYTICS_CARD_MIN_HEIGHT }}>
      <MDBox sx={DASHBOARD_CARD_HEADER_SX}>
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: READABLE_TEXT_COLOR, fontSize: "1rem" }}
        >
          {title}
        </MDTypography>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          sx={{ color: READABLE_MUTED_TEXT_COLOR, display: "block", mt: 0.35 }}
        >
          區間：{rangeLabel || "-"}
        </MDTypography>
      </MDBox>
      <MDBox
        p={2.25}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minHeight: 280,
        }}
      >
        <MDBox
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <MDBox sx={{ width: { xs: 188, sm: 220 }, height: { xs: 188, sm: 220 } }}>
            <Doughnut data={chartData} options={options} />
          </MDBox>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{
              color: DASHBOARD_TABLE_TEXT_COLOR,
              display: "block",
              textAlign: "center",
              mt: 1.5,
              fontSize: "1rem",
            }}
          >
            銷售額總計 {totalAmount}
          </MDTypography>
        </MDBox>
        <Stack spacing={1.1}>
          {details.map((item) => (
            <MDBox
              key={item.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1,
                borderTop: "1px solid #EEF3F7",
                pt: 1,
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{ color: DASHBOARD_TABLE_TEXT_COLOR, fontSize: "0.92rem", lineHeight: 1.45 }}
              >
                {item.label}
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{
                  color: DASHBOARD_TABLE_TEXT_COLOR,
                  fontSize: "0.92rem",
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  overflowWrap: "anywhere",
                }}
              >
                {item.amount} / {item.ratio}
              </MDTypography>
            </MDBox>
          ))}
        </Stack>
      </MDBox>
    </Card>
  );
}

const TAIWAN_COUNTY_MAP_SHAPES = buildTaiwanCountyMapShapes();

const interpolateHexColor = (startHex, endHex, ratio) => {
  const start = startHex.replace("#", "");
  const end = endHex.replace("#", "");
  const channels = [0, 2, 4].map((offset) => {
    const startValue = parseInt(start.slice(offset, offset + 2), 16);
    const endValue = parseInt(end.slice(offset, offset + 2), 16);
    return Math.round(startValue + (endValue - startValue) * ratio)
      .toString(16)
      .padStart(2, "0");
  });
  return `#${channels.join("")}`;
};

function TaiwanCountyMapChart({ title, rows }) {
  const countyAmountMap = useMemo(
    () =>
      new Map(
        rows
          .filter(([label]) => label && label !== UNKNOWN_COUNTY_LABEL)
          .map(([label, value]) => [normalizeCountyName(label), safeNumber(value)])
      ),
    [rows]
  );
  const countyRatioMap = useMemo(
    () =>
      new Map(
        rows
          .filter(([label]) => label && label !== UNKNOWN_COUNTY_LABEL)
          .map(([label, , ratio]) => [normalizeCountyName(label), safeNumber(ratio)])
      ),
    [rows]
  );
  const amounts = Array.from(countyAmountMap.values());
  const minAmount = amounts.length ? Math.min(...amounts) : 0;
  const maxAmount = amounts.length ? Math.max(...amounts) : 0;
  const unclassifiedAmount = rows.find(([label]) => label === UNKNOWN_COUNTY_LABEL)?.[1] || 0;

  const getCountyFill = (countyName) => {
    const amount = countyAmountMap.get(countyName);
    if (!amount) return "#DCEBFA";
    if (maxAmount === minAmount) return "#2C7EDB";
    return interpolateHexColor(
      "#D7EAFE",
      "#236AC2",
      (amount - minAmount) / (maxAmount - minAmount)
    );
  };

  return (
    <Card sx={{ height: "100%", border: "1px solid #DCE5ED", boxShadow: "none" }}>
      <MDBox
        px={2}
        py={1.25}
        sx={{ backgroundColor: "#F7FAFD", borderBottom: "1px solid #E6EDF4" }}
      >
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: READABLE_TEXT_COLOR, fontSize: "1rem" }}
        >
          {title}
        </MDTypography>
      </MDBox>
      {rows.length ? (
        <MDBox p={2}>
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "72px 1fr" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                flexDirection: { xs: "row", sm: "column" },
              }}
            >
              <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
                高
              </MDTypography>
              <MDBox
                sx={{
                  width: { xs: 88, sm: 16 },
                  height: { xs: 12, sm: 182 },
                  borderRadius: 999,
                  background: "linear-gradient(180deg, #2C7EDB 0%, #D7EAFE 100%)",
                }}
              />
              <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
                低
              </MDTypography>
            </MDBox>
            <MDBox sx={{ width: "100%", maxWidth: 300, mx: "auto" }}>
              <svg
                viewBox={`0 0 ${TAIWAN_MAP_VIEWBOX.width} ${TAIWAN_MAP_VIEWBOX.height}`}
                width="100%"
                role="img"
                aria-label="台灣縣市銷售分布地圖"
              >
                {TAIWAN_COUNTY_MAP_SHAPES.map((county) => (
                  <path
                    key={county.name}
                    d={county.path}
                    fill={getCountyFill(county.name)}
                    stroke="#FFFFFF"
                    strokeWidth="2.4"
                    strokeLinejoin="round"
                  >
                    <title>{`${county.name}: ${
                      countyAmountMap.has(county.name)
                        ? `${formatCurrency(countyAmountMap.get(county.name))} / ${Math.round(
                            countyRatioMap.get(county.name) || 0
                          )}%`
                        : "無資料"
                    }`}</title>
                  </path>
                ))}
              </svg>
            </MDBox>
          </MDBox>
          {unclassifiedAmount ? (
            <MDTypography
              variant="caption"
              sx={{ color: READABLE_MUTED_TEXT_COLOR, display: "block", mt: 1.5 }}
            >
              未分類場域金額：{formatCurrency(unclassifiedAmount)}
            </MDTypography>
          ) : null}
        </MDBox>
      ) : (
        <MDBox p={2.5}>
          <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
            目前沒有可用的縣市銷售資料。
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

function StockStatusCard({ rows }) {
  return (
    <Card sx={{ height: "100%", border: "1px solid #DCE5ED", boxShadow: "none" }}>
      <MDBox
        px={2}
        py={1.25}
        sx={{ backgroundColor: "#F7FAFD", borderBottom: "1px solid #E6EDF4" }}
      >
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ color: READABLE_TEXT_COLOR, fontSize: "1rem" }}
        >
          機台即時庫存摘要
        </MDTypography>
      </MDBox>
      <Stack spacing={1.25} sx={{ p: 2 }}>
        {rows.map((row) => (
          <MDBox
            key={row.label}
            sx={{
              backgroundColor: row.bg,
              border: "1px solid #E7EDF4",
              borderRadius: "12px",
              px: 2,
              py: 1.5,
              textAlign: "center",
            }}
          >
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: row.color }}>
              {row.value}
            </MDTypography>
            <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
              {row.label}
            </MDTypography>
          </MDBox>
        ))}
      </Stack>
    </Card>
  );
}

function FilterPanel({
  panel,
  filters,
  brandOptions,
  venueOptions,
  machineOptions,
  paymentOptions,
  regionOptions,
  onChange,
  onApply,
  onReset,
}) {
  const brandFilterOptions = useMemo(
    () => buildFilterOptions(brandOptions, "全部品牌"),
    [brandOptions]
  );
  const venueFilterOptions = useMemo(
    () => buildFilterOptions(venueOptions, "全部場域"),
    [venueOptions]
  );
  const machineFilterOptions = useMemo(
    () => buildFilterOptions(machineOptions, "全部機台"),
    [machineOptions]
  );
  const paymentFilterOptions = useMemo(
    () => buildFilterOptions(paymentOptions, "全部支付方式"),
    [paymentOptions]
  );
  const regionFilterOptions = useMemo(
    () => buildFilterOptions(regionOptions, "全部區域"),
    [regionOptions]
  );
  const [inputValues, setInputValues] = useState({
    brandId: "",
    venueId: "",
    machineId: "",
    paymentMethod: "",
    salesRegion: "",
  });

  useEffect(() => {
    const getLabelByValue = (options, value) =>
      !value ? "" : options.find((option) => option.value === value)?.label || "";

    setInputValues({
      brandId: getLabelByValue(brandFilterOptions, filters.brandId),
      venueId: getLabelByValue(venueFilterOptions, filters.venueId),
      machineId: getLabelByValue(machineFilterOptions, filters.machineId),
      paymentMethod: getLabelByValue(paymentFilterOptions, filters.paymentMethod || ""),
      salesRegion: getLabelByValue(regionFilterOptions, filters.salesRegion || ""),
    });
  }, [
    brandFilterOptions,
    filters.brandId,
    filters.machineId,
    filters.paymentMethod,
    filters.salesRegion,
    filters.venueId,
    machineFilterOptions,
    paymentFilterOptions,
    regionFilterOptions,
    venueFilterOptions,
  ]);

  const handleAutocompleteInputChange = useCallback(
    (field) => (_, value, reason) => {
      setInputValues((prev) => ({ ...prev, [field]: value }));

      if (reason === "clear") {
        onChange(field, "");
        return;
      }

      if (reason === "input" && value === "" && filters[field]) {
        onChange(field, "");
      }
    },
    [filters, onChange]
  );

  return (
    <MDBox
      px={{ xs: 2, xl: 2.5 }}
      py={2}
      sx={{ backgroundColor: "#ffffff", borderBottom: "1px solid #E6EDF4" }}
    >
      <MDBox
        sx={{
          maxWidth: DASHBOARD_CONTENT_MAX_WIDTH,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <MDBox
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "2.2fr 1.15fr 1.15fr 1fr 1fr",
            },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          <MDBox
            sx={{
              minHeight: 52,
              borderRadius: "12px",
              backgroundColor: panel.softColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
            }}
          >
            <MDTypography
              variant="button"
              fontWeight="bold"
              sx={{ color: panel.color, textAlign: "center", fontSize: "1.05rem" }}
            >
              篩選條件
            </MDTypography>
          </MDBox>
          <MDBox>
            <TextField
              fullWidth
              size="small"
              label="開始日期"
              type="date"
              value={filters.start}
              onChange={(event) => onChange("start", event.target.value)}
              sx={filterControlSx}
              InputLabelProps={{ shrink: true }}
            />
          </MDBox>
          <MDBox>
            <TextField
              fullWidth
              size="small"
              label="結束日期"
              type="date"
              value={filters.end}
              onChange={(event) => onChange("end", event.target.value)}
              sx={filterControlSx}
              InputLabelProps={{ shrink: true }}
            />
          </MDBox>
          <MDBox>
            <Autocomplete
              fullWidth
              size="small"
              options={brandFilterOptions}
              componentsProps={{
                popper: { sx: autocompletePopperSx },
                paper: { sx: { borderRadius: "12px" } },
              }}
              ListboxProps={{ sx: autocompleteListboxSx }}
              value={
                filters.brandId
                  ? brandFilterOptions.find((option) => option.value === filters.brandId) || null
                  : null
              }
              inputValue={inputValues.brandId}
              onChange={(_, option) => onChange("brandId", option?.value || "")}
              onInputChange={handleAutocompleteInputChange("brandId")}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="品牌" placeholder="全部品牌" sx={filterControlSx} />
              )}
            />
          </MDBox>
          <MDBox>
            <Autocomplete
              fullWidth
              size="small"
              options={venueFilterOptions}
              componentsProps={{
                popper: { sx: autocompletePopperSx },
                paper: { sx: { borderRadius: "12px" } },
              }}
              ListboxProps={{ sx: autocompleteListboxSx }}
              value={
                filters.venueId
                  ? venueFilterOptions.find((option) => option.value === filters.venueId) || null
                  : null
              }
              inputValue={inputValues.venueId}
              onChange={(_, option) => onChange("venueId", option?.value || "")}
              onInputChange={handleAutocompleteInputChange("venueId")}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="場域" placeholder="全部場域" sx={filterControlSx} />
              )}
            />
          </MDBox>
        </MDBox>
        <MDBox
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: panel.includePaymentFilter
                ? "2.2fr 1.15fr 1.15fr 1fr 0.95fr 0.95fr"
                : "2.2fr 1.15fr 1fr 0.95fr 0.95fr",
            },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          <MDBox>
            <Autocomplete
              fullWidth
              size="small"
              options={machineFilterOptions}
              componentsProps={{
                popper: { sx: autocompletePopperSx },
                paper: { sx: { borderRadius: "12px" } },
              }}
              ListboxProps={{ sx: autocompleteListboxSx }}
              value={
                filters.machineId
                  ? machineFilterOptions.find((option) => option.value === filters.machineId) ||
                    null
                  : null
              }
              inputValue={inputValues.machineId}
              onChange={(_, option) => onChange("machineId", option?.value || "")}
              onInputChange={handleAutocompleteInputChange("machineId")}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="機台" placeholder="全部機台" sx={filterControlSx} />
              )}
            />
          </MDBox>
          <MDBox>
            <Autocomplete
              fullWidth
              size="small"
              options={regionFilterOptions}
              componentsProps={{
                popper: { sx: autocompletePopperSx },
                paper: { sx: { borderRadius: "12px" } },
              }}
              ListboxProps={{ sx: autocompleteListboxSx }}
              value={
                filters.salesRegion
                  ? regionFilterOptions.find(
                      (option) => option.value === (filters.salesRegion || "")
                    ) || null
                  : null
              }
              inputValue={inputValues.salesRegion}
              onChange={(_, option) => onChange("salesRegion", option?.value || "")}
              onInputChange={handleAutocompleteInputChange("salesRegion")}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="區域" placeholder="全部區域" sx={filterControlSx} />
              )}
            />
          </MDBox>
          {panel.includePaymentFilter ? (
            <MDBox>
              <Autocomplete
                fullWidth
                size="small"
                options={paymentFilterOptions}
                componentsProps={{
                  popper: { sx: autocompletePopperSx },
                  paper: { sx: { borderRadius: "12px" } },
                }}
                ListboxProps={{ sx: autocompleteListboxSx }}
                value={
                  filters.paymentMethod
                    ? paymentFilterOptions.find(
                        (option) => option.value === (filters.paymentMethod || "")
                      ) || null
                    : null
                }
                inputValue={inputValues.paymentMethod}
                onChange={(_, option) => onChange("paymentMethod", option?.value || "")}
                onInputChange={handleAutocompleteInputChange("paymentMethod")}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="支付方式"
                    placeholder="全部支付方式"
                    sx={filterControlSx}
                  />
                )}
              />
            </MDBox>
          ) : null}
          <MDBox>
            <Button
              fullWidth
              variant="contained"
              onClick={onApply}
              sx={{
                minWidth: 86,
                minHeight: 56,
                borderRadius: "14px",
                fontWeight: 700,
                py: 1,
                backgroundColor: panel.color,
                color: "#FFFFFF",
              }}
            >
              查詢
            </Button>
          </MDBox>
          <MDBox>
            <Button
              fullWidth
              variant="outlined"
              onClick={onReset}
              sx={{
                minWidth: 86,
                minHeight: 56,
                borderRadius: "14px",
                borderColor: "#D3DEE8",
                color: "#536273",
                backgroundColor: "#F8FAFC",
                fontWeight: 700,
                py: 1,
              }}
            >
              重置
            </Button>
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

function PanelSection({
  panel,
  filters,
  brandOptions,
  venueOptions,
  machineOptions,
  paymentOptions,
  regionOptions,
  onChangeFilters,
  onApplyFilters,
  onResetFilters,
  data,
  loading,
  error,
}) {
  return (
    <Card
      sx={{
        height: "100%",
        border: "1px solid #CAD8E6",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 12px 30px rgba(15, 44, 78, 0.08)",
        backgroundColor: "#FBFDFF",
      }}
    >
      <MDBox
        px={2}
        py={1.5}
        sx={{
          background: `linear-gradient(180deg, ${panel.color} 0%, ${panel.color}DD 100%)`,
          color: "#ffffff",
          textAlign: "center",
        }}
      >
        <MDTypography variant="h5" color="white" fontWeight="bold">
          {panel.title}
        </MDTypography>
        <MDTypography variant="button" color="white" fontWeight="medium">
          {panel.subtitle}
        </MDTypography>
      </MDBox>

      <FilterPanel
        panel={panel}
        filters={filters}
        brandOptions={brandOptions}
        venueOptions={venueOptions}
        machineOptions={machineOptions}
        paymentOptions={paymentOptions}
        regionOptions={regionOptions}
        onChange={onChangeFilters}
        onApply={onApplyFilters}
        onReset={onResetFilters}
      />

      {error ? (
        <MDBox px={2} pt={2}>
          <Alert severity="error">{error}</Alert>
        </MDBox>
      ) : null}

      {loading ? (
        <MDBox py={8} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
      ) : (
        <MDBox p={{ xs: 2, xl: 2.5 }}>
          <MDBox sx={{ maxWidth: DASHBOARD_CONTENT_MAX_WIDTH, mx: "auto" }}>
            <Stack spacing={2}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                <KPISection
                  title="區間指標（依搜尋區間）"
                  items={data.periodKpis}
                  color={panel.color}
                />
                <KPISection
                  title="即時資訊（即時）"
                  items={data.realtimeKpis}
                  color={panel.color}
                />
              </MDBox>

              <SectionHeader title="營運分析區（依搜尋區間）" />
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "minmax(0, 1fr) minmax(0, 0.9fr) minmax(420px, 1.1fr)",
                  },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                <TimeSlotAnalysisCard
                  title="消費時段分析"
                  rangeLabel={data.rangeLabel}
                  rows={data.timeSlotRows}
                />
                <PaymentChart
                  title="支付方式佔比"
                  rangeLabel={data.paymentRangeLabel}
                  totalAmount={data.paymentTotalAmount}
                  rows={data.payments}
                  details={data.paymentDetails}
                />
                <MDBox sx={{ gridColumn: { xs: "auto", md: "1 / -1", xl: "auto" } }}>
                  <InventoryListCard rows={data.inventoryRows} />
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "minmax(0, 1.65fr) minmax(0, 1fr)",
                  },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                <DailyRevenueTrendCard rows={data.dailyTrendRows} />
                <WeekdayTrendCard
                  rangeLabel={data.rangeLabel}
                  previousRangeLabel={data.previousRangeLabel}
                  trend={data.weekdayTrend}
                />
              </MDBox>

              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                <RankingTableCard
                  title="場域 TOP 10（依銷售額）"
                  columns={[
                    { key: "rank", label: "排名", width: "10%" },
                    { key: "label", label: "場域名稱", width: "44%" },
                    { key: "orders", label: "銷售量", align: "right", width: "25%" },
                    { key: "amount", label: "銷售額", align: "right", width: "21%" },
                  ]}
                  rows={data.topVenues}
                  emptyText="目前沒有符合條件的場域資料。"
                />
                <RankingTableCard
                  title="機台 TOP 10（依銷售額）"
                  columns={[
                    { key: "rank", label: "排名", width: "10%" },
                    { key: "label", label: "場域機台", width: "44%" },
                    { key: "orders", label: "銷售量", align: "right", width: "25%" },
                    { key: "amount", label: "銷售額", align: "right", width: "21%" },
                  ]}
                  rows={data.topMachines}
                  emptyText="目前沒有符合條件的機台資料。"
                />
                <MDBox sx={{ gridColumn: { xs: "auto", md: "1 / -1", xl: "auto" } }}>
                  <RankingTableCard
                    title="商品 TOP 10（依銷售額）"
                    columns={[
                      { key: "rank", label: "排行", width: "8%" },
                      { key: "label", label: "商品名稱", width: "36%" },
                      { key: "price", label: "售價", align: "right", width: "12%" },
                      { key: "quantity", label: "銷售量", align: "right", width: "20%" },
                      { key: "amount", label: "銷售額", align: "right", width: "24%" },
                    ]}
                    rows={data.topProducts}
                    emptyText="目前沒有符合條件的商品資料。"
                  />
                </MDBox>
              </MDBox>
            </Stack>
          </MDBox>
        </MDBox>
      )}
    </Card>
  );
}

function DashboardFootnotes({ updateTime }) {
  const footnoteCardSx = {
    height: "100%",
    minWidth: 0,
    border: "1px solid #CAD8E6",
    borderRadius: "14px",
    boxShadow: "0 8px 20px rgba(15, 44, 78, 0.06)",
    backgroundColor: "#FFFFFF",
  };
  const footnoteBodySx = {
    color: READABLE_TEXT_COLOR,
    lineHeight: 1.8,
    display: "block",
    fontSize: "0.9rem",
    fontWeight: 600,
  };

  return (
    <MDBox mt={2}>
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <Card sx={footnoteCardSx}>
          <MDBox
            sx={{
              px: 3,
              py: 3,
            }}
          >
            <MDTypography
              variant="h6"
              fontWeight="bold"
              sx={{ color: "#245EA8", mb: 1, fontSize: "1.1rem" }}
            >
              指標說明
            </MDTypography>
            <MDTypography variant="button" sx={footnoteBodySx}>
              ・所有數據皆依篩選條件即時計算更新。
              <br />
              ・區間指標：依所選日期統計訂單數、銷售額、平均客單價及平均台日均(資料除以即時線上台數)。
              <br />
              ・營運分析：依區間分析消費時段、支付方式及營收趨勢。
              <br />
              ・TOP10 排行：場域、機台、商品依銷售額排序。
            </MDTypography>
          </MDBox>
        </Card>
        <Card sx={footnoteCardSx}>
          <MDBox sx={{ px: 3, py: 3 }}>
            <MDTypography
              variant="h5"
              fontWeight="bold"
              sx={{ color: "#245EA8", mb: 1, fontSize: "1.05rem" }}
            >
              即時資料說明
            </MDTypography>
            <MDTypography variant="button" sx={footnoteBodySx}>
              ・品牌數、場域數、線上機台及低庫存／缺貨機台皆為即時更新。
              <br />
              ・即時資料反映系統目前狀態，不受日期區間影響。
            </MDTypography>
          </MDBox>
        </Card>
      </MDBox>
      <Card
        sx={{
          mt: 2,
          border: "1px solid #D8E3EF",
          borderRadius: "14px",
          boxShadow: "0 8px 20px rgba(15, 44, 78, 0.05)",
          backgroundColor: "#FFFFFF",
        }}
      >
        <MDBox
          sx={{
            px: 2.5,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ color: READABLE_TEXT_COLOR, fontSize: "0.88rem" }}
          >
            資料更新時間：
            {updateTime || "即時同步"}
          </MDTypography>
          <MDTypography
            variant="button"
            sx={{ color: READABLE_MUTED_TEXT_COLOR, fontSize: "0.86rem" }}
          >
            提醒：以上數據為即時統計，實際營運請依最終結算明細表為準。
          </MDTypography>
        </MDBox>
      </Card>
    </MDBox>
  );
}

function MarketDataAnalysisBK() {
  const navigate = useNavigate();
  const { userData, roleLevel, merchantId } = useUserContext() || {};
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const numericRoleLevel = normalizeRoleLevel(roleLevel);
  const isBrandOnlyRole = numericRoleLevel === 3;
  const currentBrandScopeId = String(merchantId || "").trim();
  const backendUrl = useMemo(getGraphQLEndpoint, []);

  const [brands, setBrands] = useState([]);
  const [venues, setVenues] = useState([]);
  const [machines, setMachines] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [activePanelKey, setActivePanelKey] = useState("global");

  const [globalDraftFilters, setGlobalDraftFilters] = useState(() => createGlobalFilters());
  const [globalAppliedFilters, setGlobalAppliedFilters] = useState(() => createGlobalFilters());
  const [brandDraftFilters, setBrandDraftFilters] = useState(() => createBrandFilters());
  const [brandAppliedFilters, setBrandAppliedFilters] = useState(() => createBrandFilters());

  const [globalState, setGlobalState] = useState({
    loading: false,
    error: "",
    data: createEmptyDashboardData(),
  });
  const [brandState, setBrandState] = useState({
    loading: false,
    error: "",
    data: createEmptyDashboardData(),
  });

  useEffect(() => {
    if (!brands.length) return;
    const scopedBrands =
      isBrandOnlyRole && currentBrandScopeId
        ? brands.filter((brand) => String(brand.value || "") === currentBrandScopeId)
        : brands;
    const fallbackBrands = scopedBrands.length ? scopedBrands : brands;
    const defaultBrandId = fallbackBrands[0]?.value || "";
    setBrandDraftFilters((prev) =>
      prev.brandId && fallbackBrands.some((brand) => brand.value === prev.brandId)
        ? prev
        : { ...prev, brandId: defaultBrandId }
    );
    setBrandAppliedFilters((prev) =>
      prev.brandId && fallbackBrands.some((brand) => brand.value === prev.brandId)
        ? prev
        : { ...prev, brandId: defaultBrandId }
    );
  }, [brands, currentBrandScopeId, isBrandOnlyRole]);

  useEffect(() => {
    if (!isBrandOnlyRole) return;
    setActivePanelKey("brand");
  }, [isBrandOnlyRole]);

  const loadDashboard = useCallback(
    async (filters, panel, setter) => {
      if (!email) return;
      setter((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const data = await fetchGraphQL(backendUrl, GET_REPORT_DASHBOARD_QUERY, {
          input: buildDashboardRequestBody(email, filters, panel.includePaymentFilter),
        });
        const json = data?.GetReportDashboard || {};
        const normalizedOptions = normalizeDashboardOptions(json?.data?.options || {});

        setBrands((prev) => (normalizedOptions.brands.length ? normalizedOptions.brands : prev));
        setVenues((prev) => (normalizedOptions.venues.length ? normalizedOptions.venues : prev));
        setMachines((prev) =>
          normalizedOptions.machines.length ? normalizedOptions.machines : prev
        );
        setPaymentMethods((prev) =>
          normalizedOptions.paymentMethods.length ? normalizedOptions.paymentMethods : prev
        );
        setRegionOptions((prev) =>
          normalizedOptions.salesRegions.length ? normalizedOptions.salesRegions : prev
        );

        setter({
          loading: false,
          error: "",
          data: json?.data || createEmptyDashboardData(),
        });
      } catch (error) {
        setter({
          loading: false,
          error: error.message || "營運總覽資料載入失敗",
          data: createEmptyDashboardData(),
        });
      }
    },
    [backendUrl, email]
  );

  useEffect(() => {
    loadDashboard(globalAppliedFilters, panelConfigs.global, setGlobalState);
  }, [globalAppliedFilters, loadDashboard]);

  useEffect(() => {
    loadDashboard(brandAppliedFilters, panelConfigs.brand, setBrandState);
  }, [brandAppliedFilters, loadDashboard]);

  const brandOptions = useMemo(() => {
    const scopedBrands =
      isBrandOnlyRole && currentBrandScopeId
        ? brands.filter((brand) => String(brand.value || "") === currentBrandScopeId)
        : brands;
    const fallbackBrands = scopedBrands.length ? scopedBrands : brands;
    return fallbackBrands.slice().sort((a, b) => a.label.localeCompare(b.label));
  }, [brands, currentBrandScopeId, isBrandOnlyRole]);

  const getVenueOptions = useCallback(
    (filters) =>
      venues
        .filter((venue) => !filters.brandId || venue.brandId === filters.brandId)
        .map((venue) => ({
          value: venue.value,
          label: [venue.label, venue.brandLabel].filter(Boolean).join(" / "),
          brandId: venue.brandId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [venues]
  );

  const getMachineOptions = useCallback(
    (filters) =>
      machines
        .filter((machine) => {
          if (filters.brandId && machine.brandId !== filters.brandId) return false;
          if (filters.venueId && machine.venueId !== filters.venueId) return false;
          return true;
        })
        .map((machine) => ({
          value: machine.value,
          ref: machine.ref,
          brandId: machine.brandId,
          venueId: machine.venueId,
          label: [
            machine.label,
            machine.ref || "",
            machine.venueLabel || "",
            machine.brandLabel || "",
          ]
            .filter(Boolean)
            .join(" / "),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [machines]
  );

  const globalVenueOptions = useMemo(
    () => getVenueOptions(globalDraftFilters),
    [getVenueOptions, globalDraftFilters]
  );
  const brandVenueOptions = useMemo(
    () => getVenueOptions(brandDraftFilters),
    [brandDraftFilters, getVenueOptions]
  );
  const globalMachineOptions = useMemo(
    () => getMachineOptions(globalDraftFilters),
    [getMachineOptions, globalDraftFilters]
  );
  const brandMachineOptions = useMemo(
    () => getMachineOptions(brandDraftFilters),
    [brandDraftFilters, getMachineOptions]
  );

  const globalPaymentOptions = useMemo(
    () =>
      dedupeOptionsByLabel(
        (paymentMethods || []).map(normalizePaymentMethodOption).filter(Boolean)
      ),
    [paymentMethods]
  );

  const globalViewData = useMemo(
    () => buildViewData(globalState.data, globalAppliedFilters, navigate),
    [globalAppliedFilters, globalState.data, navigate]
  );

  const brandViewData = useMemo(
    () => buildViewData(brandState.data, brandAppliedFilters, navigate),
    [brandAppliedFilters, brandState.data, navigate]
  );

  const handleGlobalDraftChange = useCallback((key, value) => {
    setGlobalDraftFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "brandId") {
        next.venueId = "";
        next.machineId = "";
      }
      if (key === "venueId") {
        next.machineId = "";
      }
      return next;
    });
  }, []);

  const handleBrandDraftChange = useCallback((key, value) => {
    setBrandDraftFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "brandId") {
        next.venueId = "";
        next.machineId = "";
      }
      if (key === "venueId") {
        next.machineId = "";
      }
      return next;
    });
  }, []);

  const resetBrandFilters = useCallback(() => {
    const resetValue = createBrandFilters(brands[0]?.value || "");
    setBrandDraftFilters(resetValue);
    setBrandAppliedFilters(resetValue);
  }, [brands]);

  const visiblePanels = useMemo(
    () => (isBrandOnlyRole ? [panelConfigs.brand] : Object.values(panelConfigs)),
    [isBrandOnlyRole]
  );
  const globalRenderData = useMemo(
    () => (globalViewData.periodKpis.length ? globalViewData : createEmptyViewData()),
    [globalViewData]
  );
  const brandRenderData = useMemo(
    () => (brandViewData.periodKpis.length ? brandViewData : createEmptyViewData()),
    [brandViewData]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2.5}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  營銷數據分析
                </MDTypography>
              </MDBox>

              <MDBox px={2} py={3} sx={{ backgroundColor: "#EEF3F8" }}>
                <>
                  {visiblePanels.length > 1 ? (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      sx={{ px: { xs: 0, sm: 0 }, pb: 2 }}
                    >
                      {visiblePanels.map((panel) => {
                        const isActive = activePanelKey === panel.key;
                        return (
                          <Button
                            key={panel.key}
                            variant={isActive ? "contained" : "outlined"}
                            onClick={() => setActivePanelKey(panel.key)}
                            sx={{
                              flex: 1,
                              borderRadius: "12px",
                              py: 1.25,
                              px: 2,
                              justifyContent: "flex-start",
                              textAlign: "left",
                              borderColor: isActive ? panel.color : "#C8D6E5",
                              backgroundColor: isActive ? panel.color : "#FFFFFF",
                              color: isActive ? "#FFFFFF" : "#35506B",
                            }}
                          >
                            <MDBox>
                              <MDTypography
                                variant="button"
                                fontWeight="bold"
                                color={isActive ? "white" : "text"}
                              >
                                {panel.title}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                display="block"
                                color={isActive ? "white" : "text"}
                                fontWeight="medium"
                                sx={{ opacity: 1 }}
                              >
                                {panel.subtitle}
                              </MDTypography>
                            </MDBox>
                          </Button>
                        );
                      })}
                    </Stack>
                  ) : null}

                  {activePanelKey === "global" ? (
                    <PanelSection
                      panel={panelConfigs.global}
                      filters={globalDraftFilters}
                      brandOptions={brandOptions}
                      venueOptions={globalVenueOptions}
                      machineOptions={globalMachineOptions}
                      paymentOptions={globalPaymentOptions}
                      regionOptions={regionOptions}
                      onChangeFilters={handleGlobalDraftChange}
                      onApplyFilters={() =>
                        setGlobalAppliedFilters(normalizePanelFilters(globalDraftFilters))
                      }
                      onResetFilters={() => {
                        const resetValue = createGlobalFilters();
                        setGlobalDraftFilters(resetValue);
                        setGlobalAppliedFilters(resetValue);
                      }}
                      data={globalRenderData}
                      loading={globalState.loading}
                      error={globalState.error}
                    />
                  ) : (
                    <PanelSection
                      panel={panelConfigs.brand}
                      filters={brandDraftFilters}
                      brandOptions={brandOptions}
                      venueOptions={brandVenueOptions}
                      machineOptions={brandMachineOptions}
                      paymentOptions={[]}
                      regionOptions={regionOptions}
                      onChangeFilters={handleBrandDraftChange}
                      onApplyFilters={() =>
                        setBrandAppliedFilters(normalizePanelFilters(brandDraftFilters))
                      }
                      onResetFilters={resetBrandFilters}
                      data={brandRenderData}
                      loading={brandState.loading}
                      error={brandState.error}
                    />
                  )}
                </>
              </MDBox>

              <MDBox px={2} pb={3} sx={{ backgroundColor: "#EEF3F8" }}>
                <DashboardFootnotes
                  updateTime={
                    (activePanelKey === "global"
                      ? globalState.data?.update_dt
                      : brandState.data?.update_dt) || "即時同步"
                  }
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

const kpiItemShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  note: PropTypes.string,
  linkNote: PropTypes.string,
  onLinkClick: PropTypes.func,
  noteMeta: PropTypes.shape({
    prefix: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
});

const rankRowsShape = PropTypes.arrayOf(
  PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
);

const stockStatusShape = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    bg: PropTypes.string.isRequired,
  })
);

KPIItem.propTypes = {
  item: kpiItemShape.isRequired,
  color: PropTypes.string.isRequired,
};

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
};

KPISection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(kpiItemShape).isRequired,
  color: PropTypes.string.isRequired,
};

TimeSlotAnalysisCard.propTypes = {
  title: PropTypes.string.isRequired,
  rangeLabel: PropTypes.string,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      ratio: PropTypes.number.isRequired,
    })
  ).isRequired,
};

TimeSlotAnalysisCard.defaultProps = {
  rangeLabel: "",
};

InventoryListCard.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      machine: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      qty: PropTypes.string.isRequired,
    })
  ).isRequired,
};

DailyRevenueTrendCard.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      weekday: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      orders: PropTypes.number.isRequired,
    })
  ).isRequired,
};

WeekdayTrendCard.propTypes = {
  rangeLabel: PropTypes.string,
  previousRangeLabel: PropTypes.string,
  trend: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    current: PropTypes.arrayOf(PropTypes.number).isRequired,
    previous: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
};

WeekdayTrendCard.defaultProps = {
  rangeLabel: "",
  previousRangeLabel: "",
};

RankingTableCard.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  emptyText: PropTypes.string.isRequired,
};

RankCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  rows: rankRowsShape.isRequired,
  accent: PropTypes.string.isRequired,
  emptyText: PropTypes.string.isRequired,
};

RankCard.defaultProps = {
  subtitle: "",
};

PaymentChart.propTypes = {
  title: PropTypes.string.isRequired,
  rows: rankRowsShape.isRequired,
  rangeLabel: PropTypes.string,
  totalAmount: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
      ratio: PropTypes.string.isRequired,
    })
  ).isRequired,
};

PaymentChart.defaultProps = {
  rangeLabel: "",
  totalAmount: "NT$ 0",
};

TaiwanCountyMapChart.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ).isRequired,
};

StockStatusCard.propTypes = {
  rows: stockStatusShape.isRequired,
};

DashboardFootnotes.propTypes = {
  updateTime: PropTypes.string,
};

DashboardFootnotes.defaultProps = {
  updateTime: "",
};

FilterPanel.propTypes = {
  panel: PropTypes.shape({
    color: PropTypes.string.isRequired,
    softColor: PropTypes.string.isRequired,
    includePaymentFilter: PropTypes.bool.isRequired,
  }).isRequired,
  filters: PropTypes.shape({
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired,
    brandId: PropTypes.string,
    venueId: PropTypes.string,
    machineId: PropTypes.string,
    paymentMethod: PropTypes.string,
    salesRegion: PropTypes.string,
  }).isRequired,
  brandOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  venueOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  machineOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  paymentOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  regionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

PanelSection.propTypes = {
  panel: PropTypes.shape({
    key: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    softColor: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    includePaymentFilter: PropTypes.bool.isRequired,
  }).isRequired,
  filters: FilterPanel.propTypes.filters,
  brandOptions: FilterPanel.propTypes.brandOptions,
  venueOptions: FilterPanel.propTypes.venueOptions,
  machineOptions: FilterPanel.propTypes.machineOptions,
  paymentOptions: FilterPanel.propTypes.paymentOptions,
  regionOptions: FilterPanel.propTypes.regionOptions,
  onChangeFilters: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  data: PropTypes.shape({
    periodKpis: PropTypes.arrayOf(kpiItemShape).isRequired,
    realtimeKpis: PropTypes.arrayOf(kpiItemShape).isRequired,
    timeSlotRows: PropTypes.array.isRequired,
    dailyTrendRows: PropTypes.array.isRequired,
    weekdayTrend: PropTypes.object.isRequired,
    payments: rankRowsShape.isRequired,
    paymentDetails: PropTypes.array.isRequired,
    paymentRangeLabel: PropTypes.string.isRequired,
    paymentTotalAmount: PropTypes.string.isRequired,
    inventoryRows: PropTypes.array.isRequired,
    topVenues: PropTypes.array.isRequired,
    topMachines: PropTypes.array.isRequired,
    topProducts: PropTypes.array.isRequired,
    rangeLabel: PropTypes.string.isRequired,
    previousRangeLabel: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
};

PanelSection.defaultProps = {
  filters: undefined,
  brandOptions: undefined,
  venueOptions: undefined,
  machineOptions: undefined,
  paymentOptions: undefined,
  regionOptions: undefined,
};

export default MarketDataAnalysisBK;
